import { useEffect } from 'react'
import { useWidget } from '../../providers/widget'
import type { GalleryWidgetConfig } from './config'
import { useGallery } from './providers'
import { useGalleryTour } from './use-tour'
import { GALLERY_OPEN_AT_EVENT, galleryOwnsRequest, resolveOpenAtIndex, type GalleryOpenAtRequest } from './open-at'

/**
 * Answer open-at requests from other widgets on the page (see ./open-at.ts).
 * Mounted once per gallery inside GalleryProvider; reads the same React
 * Query entry every gallery component reads, so it adds no request. Only
 * the first gallery that owns the property and finds the target takes a
 * request (a taken event is left alone by the others).
 */
export function useGalleryOpenAt(): void {
  const { options } = useWidget<GalleryWidgetConfig>()
  const { setFullScreenImageIndex } = useGallery()
  const { images, tour } = useGalleryTour()
  const { feed_id, property } = options.filter

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onRequest = (event: Event) => {
      if (event.defaultPrevented) return
      const request = (event as CustomEvent<GalleryOpenAtRequest>).detail
      if (!galleryOwnsRequest({ feed_id, property }, request)) return
      const index = resolveOpenAtIndex(images, tour, request)
      if (index === undefined) return
      event.preventDefault()
      setFullScreenImageIndex(index)
    }
    window.addEventListener(GALLERY_OPEN_AT_EVENT, onRequest)
    return () => window.removeEventListener(GALLERY_OPEN_AT_EVENT, onRequest)
  }, [feed_id, property, images, tour, setFullScreenImageIndex])
}
